import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../../context/SupabaseAppContext';
import { Task } from '../../types';
import { 
  PlusIcon,
  Squares2X2Icon,
  TableCellsIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  CalendarIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  PaperClipIcon
} from '@heroicons/react/24/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTasks, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import AttachmentList from '../Attachments/AttachmentList';
import CommentForm from '../Comments/CommentForm';
import CommentList from '../Comments/CommentList';
import Pagination from '../Common/Pagination';

interface TasksManagementProps {
  onShowTaskForm?: () => void;
  onEditTask?: (task: Task) => void;
}

type ViewMode = 'table' | 'kanban';

const TasksManagement: React.FC<TasksManagementProps> = ({ onShowTaskForm, onEditTask }) => {
  const { state } = useApp();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentRefreshTrigger, setCommentRefreshTrigger] = useState(0);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Helper functions
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'To Do': return 'bg-blue-500';
      case 'In Progress': return 'bg-purple-500';
      case 'Done': return 'bg-emerald-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'To Do': return 'bg-blue-100 text-blue-800';
      case 'In Progress': return 'bg-purple-100 text-purple-800';
      case 'Done': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'High': return '🔴';
      case 'Medium': return '🟡';
      case 'Low': return '🟢';
      default: return '⚪';
    }
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
  };

  const closePanel = () => {
    setSelectedTask(null);
    setShowCommentForm(false);
  };

  const handleEditTask = () => {
    if (selectedTask && onEditTask) {
      onEditTask(selectedTask);
      closePanel(); // Close the detail panel
    }
  };

  // Comment handlers
  const handleAddComment = () => {
    setShowCommentForm(true);
  };

  const handleCommentAdded = () => {
    setShowCommentForm(false);
    setCommentRefreshTrigger(prev => prev + 1);
  };

  const handleCancelComment = () => {
    setShowCommentForm(false);
  };

  // Handle Escape key to close panel
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedTask) {
        closePanel();
      }
    };

    if (selectedTask) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedTask]);

  // Get linked idea for a task
  const getLinkedIdea = (task: Task) => {
    if (task.linkedIdeaId) {
      return state.ideas.find(idea => idea.id === task.linkedIdeaId);
    }
    return null;
  };

  // Filter and sort tasks
  const filteredTasks = useMemo(() => {
    let filtered = state.tasks;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.uniqueId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (task.description && task.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }

    // Assignee filter
    if (assigneeFilter !== 'all') {
      filtered = filtered.filter(task => task.assignee?.id === assigneeFilter);
    }

    return filtered;
  }, [state.tasks, searchQuery, statusFilter, priorityFilter, assigneeFilter]);

  // Pagination logic
  const totalFilteredTasks = filteredTasks.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTasks = filteredTasks.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, priorityFilter, assigneeFilter]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Group tasks by status for Kanban view (use all filtered tasks for kanban)
  const kanbanGroups = useMemo(() => {
    const statuses = ['To Do', 'In Progress', 'Done'];
    return statuses.map(status => ({
      status,
      tasks: filteredTasks.filter(task => task.status === status),
      color: getStatusColor(status)
    }));
  }, [filteredTasks]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <FontAwesomeIcon icon={faTasks} className="mr-3 text-deep-teal" />
            Task Management
          </h1>
          <p className="text-gray-600 mt-2">
            Create, Track, and Prioritize Idea Tasks
          </p>
        </div>
        <div className="mt-4 lg:mt-0 flex items-center space-x-4">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-white text-deep-teal shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <TableCellsIcon className="h-4 w-4 mr-2" />
              Table
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-white text-deep-teal shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4 mr-2" />
              Kanban
            </button>
          </div>
          
          {onShowTaskForm && (
            <button 
              onClick={onShowTaskForm}
              className="btn-aqua flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              New Task
            </button>
          )}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
        {/* Search */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field min-w-32"
            >
              <option value="all">All Statuses</option>
              <option value="To Do">To Do</option>
              <option value="In Progress">In Progress</option>
              <option value="Done">Done</option>
            </select>
          </div>
          
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="input-field min-w-0"
          >
            <option value="all">All Priorities</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>

          <select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            className="input-field min-w-0"
          >
            <option value="all">All Assignees</option>
            {state.users.map(user => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full">
        {viewMode === 'table' ? (
          /* Table View */
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Task
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assignee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedTasks.map((task) => {
                    const linkedIdea = getLinkedIdea(task);
                    return (
                      <tr
                        key={task.id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleTaskClick(task)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`flex-shrink-0 h-2 w-2 rounded-full mr-3 ${getStatusColor(task.status)}`}></div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <div className="text-sm font-medium text-gray-900">{task.title}</div>
                                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-mono">
                                  {task.uniqueId}
                                </span>
                              </div>
                              {task.description && (
                                <div className="text-sm text-gray-500 truncate max-w-xs">{task.description}</div>
                              )}
                              {linkedIdea && (
                                <div className="flex items-center mt-1">
                                  <FontAwesomeIcon icon={faLightbulb} className="h-3 w-3 text-electric-lime mr-1" />
                                  <span className="text-xs text-gray-500">{linkedIdea.title}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(task.status)}`}>
                            {task.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="mr-1">{getPriorityIcon(task.priority)}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {task.assignee ? (
                            <div className="flex items-center">
                              <span className="text-sm mr-2">{task.assignee.avatar}</span>
                              <span className="text-sm text-gray-900">{task.assignee.name}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">Unassigned</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {task.dueDate ? format(new Date(task.dueDate), 'MMM d, yyyy') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(task.dateCreated), 'MMM d, yyyy')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {task.comments.length > 0 && (
                              <div className="flex items-center text-gray-400">
                                <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
                                <span className="text-xs">{task.comments.length}</span>
                              </div>
                            )}
                            {task.attachments.length > 0 && (
                              <div className="flex items-center text-gray-400">
                                <PaperClipIcon className="h-4 w-4 mr-1" />
                                <span className="text-xs">{task.attachments.length}</span>
                              </div>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTaskClick(task);
                              }}
                              className="text-deep-teal hover:text-deep-teal/80"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {totalFilteredTasks === 0 && (
              <div className="text-center py-12">
                <FontAwesomeIcon icon={faTasks} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
                <p className="text-gray-500 mb-4">
                  {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all'
                    ? 'Try adjusting your filters or search query.'
                    : 'Get started by creating your first task.'}
                </p>
                {onShowTaskForm && (
                  <button onClick={onShowTaskForm} className="btn-primary">
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create First Task
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Kanban View */
          <div className="overflow-x-auto">
            <div className="flex space-x-4 min-w-max pb-4">
          {kanbanGroups.map(({ status, tasks, color }) => (
            <div key={status} className="flex-shrink-0 w-80">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className={`px-4 py-3 rounded-t-lg ${color} text-white`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">{status}</h3>
                    <span className="bg-white bg-opacity-20 text-xs px-2 py-1 rounded-full">
                      {tasks.length}
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  {tasks.map((task) => {
                    const linkedIdea = getLinkedIdea(task);
                    return (
                      <div
                        key={task.id}
                        className="bg-gray-50 rounded-lg p-4 cursor-pointer hover:bg-gray-100 transition-colors border border-gray-200"
                        onClick={() => handleTaskClick(task)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-900 line-clamp-2">
                            {task.title}
                          </h4>
                          <div className="flex items-center ml-2">
                            <span className="text-xs mr-1">{getPriorityIcon(task.priority)}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </span>
                          </div>
                        </div>
                        
                        {task.description && (
                          <p className="text-xs text-gray-600 line-clamp-2 mb-3">
                            {task.description}
                          </p>
                        )}
                        
                        {linkedIdea && (
                          <div className="flex items-center mb-3 p-2 bg-electric-lime bg-opacity-10 rounded">
                            <FontAwesomeIcon icon={faLightbulb} className="h-3 w-3 text-electric-lime mr-1" />
                            <span className="text-xs text-green-800 truncate">{linkedIdea.title}</span>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {task.assignee ? (
                              <>
                                <span className="text-xs">{task.assignee.avatar}</span>
                                <span className="text-xs text-gray-500">{task.assignee.name}</span>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">Unassigned</span>
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                            {task.comments.length > 0 && (
                              <div className="flex items-center text-gray-400">
                                <ChatBubbleLeftIcon className="h-3 w-3 mr-1" />
                                <span className="text-xs">{task.comments.length}</span>
                              </div>
                            )}
                            {task.attachments.length > 0 && (
                              <div className="flex items-center text-gray-400">
                                <PaperClipIcon className="h-3 w-3 mr-1" />
                                <span className="text-xs">{task.attachments.length}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {task.dueDate && (
                          <div className="flex items-center mt-2 text-xs text-gray-500">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            Due {format(new Date(task.dueDate), 'MMM d')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {tasks.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <FontAwesomeIcon icon={faTasks} className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No tasks in {status.toLowerCase()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>
        )}
      </div>

      {/* Pagination for Table View */}
      {viewMode === 'table' && totalFilteredTasks > 0 && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalItems={totalFilteredTasks}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemName="tasks"
          />
        </div>
      )}

      {/* Slide-over Panel */}
      {selectedTask && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity z-40"
            onClick={closePanel}
          />
          
          {/* Slide-over Panel */}
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 overflow-y-auto">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="bg-deep-teal px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Task Details</h3>
                <button
                  onClick={closePanel}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              {/* Content */}
              <div className="flex-1 px-6 py-6">
                <div className="flex items-center space-x-3 mb-6">
                  <h4 className="text-2xl font-bold text-gray-900">{selectedTask.title}</h4>
                  <span className="text-sm px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-mono">
                    {selectedTask.uniqueId}
                  </span>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Status</h5>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(selectedTask.status)}`}>
                      {selectedTask.status}
                    </span>
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Priority</h5>
                    <div className="flex items-center">
                      <span className="mr-2">{getPriorityIcon(selectedTask.priority)}</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(selectedTask.priority)}`}>
                        {selectedTask.priority}
                      </span>
                    </div>
                  </div>

                  {selectedTask.description && (
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-3">Description</h5>
                      <p className="text-gray-600 leading-relaxed">{selectedTask.description}</p>
                    </div>
                  )}

                  {/* Attachments Section */}
                  <div>
                    <AttachmentList
                      taskId={selectedTask.id}
                      className="bg-gray-50 rounded-lg p-4"
                    />
                  </div>

                  {/* Comments Section */}
                  <div>
                    <CommentList
                      taskId={selectedTask.id}
                      title="Comments"
                      className="bg-gray-50 rounded-lg p-4"
                      refreshTrigger={commentRefreshTrigger}
                    />
                    
                    {showCommentForm && (
                      <div className="mt-4">
                        <CommentForm
                          taskId={selectedTask.id}
                          onCommentAdded={handleCommentAdded}
                          onCancel={handleCancelComment}
                          placeholder="Share your thoughts about this task..."
                        />
                      </div>
                    )}
                  </div>

                  {selectedTask.assignee && (
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-3">Assignee</h5>
                      <div className="flex items-center bg-gray-50 rounded-lg p-3">
                        <span className="text-2xl mr-3">{selectedTask.assignee.avatar}</span>
                        <div>
                          <div className="font-medium text-gray-900">{selectedTask.assignee.name}</div>
                          <div className="text-sm text-gray-500">Task Assignee</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {getLinkedIdea(selectedTask) && (
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-3">Linked Idea</h5>
                      <div className="flex items-center bg-electric-lime bg-opacity-10 rounded-lg p-3">
                        <FontAwesomeIcon icon={faLightbulb} className="h-5 w-5 text-electric-lime mr-3" />
                        <div>
                          <div className="font-medium text-green-800">{getLinkedIdea(selectedTask)!.title}</div>
                          <div className="text-sm text-green-600">Related Product Idea</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-3">Created</h5>
                      <div className="flex items-center text-gray-600">
                        <CalendarIcon className="h-5 w-5 mr-2" />
                        {format(new Date(selectedTask.dateCreated), 'MMM d, yyyy')}
                      </div>
                    </div>
                    {selectedTask.dueDate && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">Due Date</h5>
                        <div className="flex items-center text-gray-600">
                          <ClockIcon className="h-5 w-5 mr-2" />
                          {format(new Date(selectedTask.dueDate), 'MMM d, yyyy')}
                        </div>
                      </div>
                    )}
                  </div>


                </div>
              </div>
              
              {/* Footer Actions */}
              <div className="border-t border-gray-200 px-6 py-4">
                <div className="flex space-x-3">
                  <button 
                    onClick={handleEditTask}
                    className="flex-1 bg-deep-teal text-white px-4 py-2 rounded-lg hover:bg-deep-teal/90 transition-colors"
                  >
                    <PencilIcon className="h-4 w-4 inline mr-2" />
                    Edit Task
                  </button>
                  <button 
                    onClick={handleAddComment}
                    className="flex-1 bg-deep-teal text-white px-4 py-2 rounded-lg hover:bg-deep-teal/90 transition-colors"
                  >
                    <ChatBubbleLeftIcon className="h-4 w-4 inline mr-2" />
                    Add Comment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TasksManagement; 